import { createTheme } from "@mui/material/styles";

/**
 * tykhere enterprise design system — MUI theme.
 *
 * This is the single source of truth for the app's look & feel across every
 * MUI-rendered screen. It refines the existing indigo/violet brand into a
 * disciplined, enterprise-grade palette with consistent typography, spacing,
 * radius and elevation. Wrapping <App/> in <ThemeProvider theme={theme}> lets
 * all MUI components inherit these tokens automatically.
 *
 * Note: CssBaseline is intentionally NOT applied so we don't disrupt the
 * existing Bootstrap / custom CSS layout. Global CSS tokens live in index.css.
 */

// Brand — refined indigo/violet (derived from the original #350DFF→#8067FD gradient)
const brand = {
  50: "#EEEBFF",
  100: "#D9D2FF",
  200: "#B7A9FF",
  300: "#9481FF",
  400: "#7660FF",
  500: "#5B4BFF", // primary
  600: "#4B37F0",
  700: "#3B2ACC",
  800: "#2E2199",
  900: "#221A73",
};

// Accent — refined gold (from the original rgb(255,160,0))
const gold = {
  light: "#FFC24D",
  main: "#F5A300",
  dark: "#C98500",
  contrastText: "#1A1A1A",
};

// Neutral slate scale for text, borders, surfaces
const neutral = {
  50: "#F8F9FB",
  100: "#F1F3F7",
  200: "#E4E7EF",
  300: "#CFD4E0",
  400: "#9AA2B4",
  500: "#6B7385",
  600: "#4B5162",
  700: "#343A49",
  800: "#232833",
  900: "#151922",
};

const theme = createTheme({
  palette: {
    primary: {
      light: brand[300],
      main: brand[500],
      dark: brand[700],
      contrastText: "#FFFFFF",
    },
    secondary: {
      light: gold.light,
      main: gold.main,
      dark: gold.dark,
      contrastText: gold.contrastText,
    },
    success: { main: "#16A34A", light: "#DCFCE7", dark: "#15803D", contrastText: "#FFFFFF" },
    warning: { main: "#F59E0B", light: "#FEF3C7", dark: "#B45309", contrastText: "#1A1A1A" },
    error: { main: "#DC2626", light: "#FEE2E2", dark: "#B91C1C", contrastText: "#FFFFFF" },
    info: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8", contrastText: "#FFFFFF" },
    grey: neutral,
    text: {
      primary: neutral[800],
      secondary: neutral[500],
      disabled: neutral[400],
    },
    background: {
      default: "#F7F8FC",
      paper: "#FFFFFF",
    },
    divider: neutral[200],
  },

  shape: { borderRadius: 12 },

  typography: {
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h1: { fontWeight: 700, letterSpacing: "-0.02em" },
    h2: { fontWeight: 700, letterSpacing: "-0.02em" },
    h3: { fontWeight: 700, letterSpacing: "-0.01em" },
    h4: { fontWeight: 700, letterSpacing: "-0.01em" },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
  },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 18,
          paddingBlock: 8,
          boxShadow: "none",
          transition: "all .18s ease",
        },
        containedPrimary: {
          "&:hover": {
            boxShadow: "0 6px 16px rgba(91,75,255,0.28)",
          },
        },
        outlined: { borderWidth: 1.5, "&:hover": { borderWidth: 1.5 } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 16 },
        elevation1: { boxShadow: "0 1px 3px rgba(21,25,34,0.06), 0 1px 2px rgba(21,25,34,0.04)" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${neutral[200]}`,
          boxShadow: "0 1px 3px rgba(21,25,34,0.05)",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#FFFFFF",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: neutral[300] },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: neutral[400] },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: brand[500],
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 8, fontWeight: 600 } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 18 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[800],
          fontSize: 12,
          borderRadius: 8,
          padding: "6px 10px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: { fontWeight: 700, color: neutral[600], backgroundColor: neutral[50] },
      },
    },
  },
});

export default theme;
